/* ===== Activities: photo gallery, compressed in the browser ===== */

import { useCallback, useEffect, useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import type { EventPhoto } from '@zetyar/domain'
import { Download, ImagePlus, Trash2, X } from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Modal } from '@/presentation/components/ui/Modal'
import { webEventRepository } from '@/infrastructure/repositories/events'
import {
  ACCEPTED_PHOTO_TYPES,
  formatPhotoSize,
  removeEventPhotoObjects,
  uploadEventPhoto,
} from '@/infrastructure/storage/eventPhotos'
import {
  buildPhotosZip,
  photoFileName,
  photosZipName,
  saveBlob,
  triggerDownload,
} from '@/infrastructure/storage/eventPhotoDownload'

const MAX_PHOTOS = 30

interface EventPhotosSectionProps {
  readonly context: TenantContext
  readonly eventId: string
  readonly eventTitle: string
  readonly canManage: boolean
}

function uploadErrorMessage(error: unknown): string {
  const message = typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : ''
  if (message.includes('event_photos_limit_reached')) return `Esta actividad ya tiene ${MAX_PHOTOS} fotos, el máximo.`
  if (message.includes('event_photos_too_large')) return 'La foto quedó demasiado pesada incluso comprimida.'
  if (message.includes('event_photos_forbidden') || message.toLowerCase().includes('row-level security')) {
    return 'Tu rol no permite agregar fotos a las actividades.'
  }
  // Validation errors thrown by the compressor already carry a readable message.
  return message && message.length < 160 ? message : 'No se pudo subir la foto.'
}

export function EventPhotosSection({ context, eventId, eventTitle, canManage }: EventPhotosSectionProps) {
  const [photos, setPhotos] = useState<EventPhoto[]>([])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<{ url: string; photo: EventPhoto; index: number } | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [zipProgress, setZipProgress] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { listPhotos, getPhotoUrls } = webEventRepository
    if (!listPhotos) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const items = await listPhotos(context, eventId)
      setPhotos(items)
      // Only thumbnails are signed up front; the full size is requested when a photo is opened.
      setUrls(getPhotoUrls && items.length > 0 ? await getPhotoUrls(context, items.map((photo) => photo.thumbPath)) : {})
    } catch (caught) {
      console.error('Error al cargar las fotos de la actividad:', caught)
      setError('No se pudieron cargar las fotos.')
    } finally {
      setIsLoading(false)
    }
  }, [context, eventId])

  useEffect(() => {
    void load()
  }, [load])

  const upload = async (files: FileList | null) => {
    const registerPhoto = webEventRepository.registerPhoto
    if (!files || files.length === 0 || !registerPhoto) return

    const selected = Array.from(files).slice(0, MAX_PHOTOS - photos.length)
    if (selected.length === 0) {
      setError(`Esta actividad ya tiene ${MAX_PHOTOS} fotos, el máximo.`)
      return
    }

    setError(null)
    setSavedNote(null)
    let originalTotal = 0
    let storedTotal = 0

    try {
      for (const [index, file] of selected.entries()) {
        setProgress(`Procesando ${index + 1} de ${selected.length}...`)
        const uploaded = await uploadEventPhoto({ organizationId: context.organizationId, eventId, file })
        try {
          await registerPhoto(context, { eventId, ...uploaded })
        } catch (caught) {
          // The row is the source of truth: if it fails, do not leave the objects behind.
          await removeEventPhotoObjects([uploaded.storagePath, uploaded.thumbPath]).catch(() => undefined)
          throw caught
        }
        originalTotal += uploaded.originalBytes
        storedTotal += uploaded.byteSize + uploaded.thumbByteSize
      }
      setSavedNote(`${selected.length} foto(s): ${formatPhotoSize(originalTotal)} en el celular, ${formatPhotoSize(storedTotal)} guardados.`)
    } catch (caught) {
      console.error('Error al subir fotos de la actividad:', caught)
      setError(uploadErrorMessage(caught))
    } finally {
      setProgress(null)
      await load()
    }
  }

  const remove = async (photo: EventPhoto) => {
    const deletePhoto = webEventRepository.deletePhoto
    if (!deletePhoto) return
    if (!confirm('¿Eliminar esta foto?')) return

    setError(null)
    try {
      await deletePhoto(context, photo.id)
      await removeEventPhotoObjects([photo.storagePath, photo.thumbPath]).catch(() => undefined)
      setPhotos((current) => current.filter((item) => item.id !== photo.id))
    } catch (caught) {
      console.error('Error al eliminar la foto:', caught)
      setError('No se pudo eliminar la foto.')
    }
  }

  const openPhoto = async (photo: EventPhoto, index: number) => {
    const getPhotoUrls = webEventRepository.getPhotoUrls
    if (!getPhotoUrls) return
    try {
      const full = await getPhotoUrls(context, [photo.storagePath])
      const url = full[photo.storagePath]
      if (url) setLightbox({ url, photo, index })
    } catch (caught) {
      console.error('Error al abrir la foto:', caught)
      setError('No se pudo abrir la foto.')
    }
  }

  const downloadPhoto = async (photo: EventPhoto, index: number) => {
    const getPhotoDownloadUrl = webEventRepository.getPhotoDownloadUrl
    if (!getPhotoDownloadUrl) return
    setIsDownloading(true)
    setError(null)
    try {
      const url = await getPhotoDownloadUrl(context, photo.storagePath, photoFileName(eventTitle, index, photo.storagePath))
      triggerDownload(url)
    } catch (caught) {
      console.error('Error al descargar la foto:', caught)
      setError('No se pudo descargar la foto.')
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadAll = async () => {
    const getPhotoUrls = webEventRepository.getPhotoUrls
    if (!getPhotoUrls || photos.length === 0 || zipProgress) return
    setError(null)
    setZipProgress('Preparando...')
    try {
      const fullUrls = await getPhotoUrls(context, photos.map((photo) => photo.storagePath))
      const files = photos.flatMap((photo, index) => {
        const url = fullUrls[photo.storagePath]
        return url ? [{ name: photoFileName(eventTitle, index, photo.storagePath), url }] : []
      })
      const zip = await buildPhotosZip(files, (done, total) => setZipProgress(`Preparando ${done} de ${total}...`))
      saveBlob(zip, photosZipName(eventTitle))
    } catch (caught) {
      console.error('Error al descargar las fotos:', caught)
      setError('No se pudieron descargar las fotos.')
    } finally {
      setZipProgress(null)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          Fotos {photos.length > 0 && `(${photos.length}/${MAX_PHOTOS})`}
        </span>
        <div className="flex flex-wrap items-center gap-2">
        {photos.length > 0 && (
          <button
            type="button"
            onClick={() => void downloadAll()}
            disabled={Boolean(zipProgress)}
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-neutral-200 px-2.5 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
          >
            <Download className="h-3.5 w-3.5" />
            {zipProgress ?? 'Descargar todas'}
          </button>
        )}
        {canManage && photos.length < MAX_PHOTOS && (
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border border-neutral-200 px-2.5 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800">
            <ImagePlus className="h-3.5 w-3.5" />
            {progress ?? 'Agregar fotos'}
            <input
              type="file"
              multiple
              accept={ACCEPTED_PHOTO_TYPES.join(',')}
              className="sr-only"
              disabled={Boolean(progress)}
              onChange={(event) => {
                void upload(event.target.files)
                event.target.value = ''
              }}
            />
          </label>
        )}
        </div>
      </div>

      {error && <p role="alert" className="text-xs text-danger-600 dark:text-danger-300">{error}</p>}
      {savedNote && <p className="text-xs text-success-700 dark:text-success-200">{savedNote}</p>}

      {isLoading ? (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">Cargando fotos...</p>
      ) : photos.length === 0 ? (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {canManage ? 'Aún no hay fotos. Se comprimen al subirlas.' : 'Aún no hay fotos de esta actividad.'}
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((photo, index) => (
            <li key={photo.id} className="group relative">
              <button
                type="button"
                onClick={() => void openPhoto(photo, index)}
                className="block w-full overflow-hidden rounded-[10px] border border-neutral-200 dark:border-neutral-800"
                aria-label={photo.caption ?? 'Ver foto de la actividad'}
              >
                <img
                  src={urls[photo.thumbPath]}
                  alt={photo.caption ?? ''}
                  loading="lazy"
                  className="aspect-square w-full bg-neutral-100 object-cover dark:bg-neutral-800"
                />
              </button>
              {canManage && (
                <button
                  type="button"
                  onClick={() => void remove(photo)}
                  aria-label="Eliminar foto"
                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-danger-600 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 dark:bg-neutral-900/90"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal isOpen={Boolean(lightbox)} onClose={() => setLightbox(null)} title={lightbox?.photo.caption ?? 'Foto de la actividad'} size="xl">
        {lightbox && (
          <div className="space-y-3">
            <img src={lightbox.url} alt={lightbox.photo.caption ?? ''} className="max-h-[70vh] w-full rounded-[13px] object-contain" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {lightbox.photo.width} x {lightbox.photo.height} px, {formatPhotoSize(lightbox.photo.byteSize)}. Es la versión
              comprimida: el original del celular no se guarda.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" leftIcon={<X className="h-4 w-4" />} onClick={() => setLightbox(null)}>
                Cerrar
              </Button>
              <Button
                leftIcon={<Download className="h-4 w-4" />}
                isLoading={isDownloading}
                onClick={() => void downloadPhoto(lightbox.photo, lightbox.index)}
              >
                Descargar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
